/**
 * Shared compliance UI components
 *
 * Reusable components for displaying task metadata, status, badges, and avatars
 * throughout the compliance module.
 */

import type { ReactNode } from 'react';
import { Calendar, Check, X, Clock, AlertCircle, AlertTriangle, Circle, CircleOff, HelpCircle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, isPast, isToday } from 'date-fns';
import { MemberAvatar } from '@/components/compliance/MemberAvatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@deepguide-ai/ui/tooltip';
import type { ControlEvidenceStatusEnum, ApiCheckHealth, TestEvidencePreparation } from '@deepguide-ai/types';
import { getStatusLabel, getStatusColor } from '@/lib/compliance-status';
import { statusTextClasses, type StatusColor } from '@/lib/status-colors';
import {
  ANCHOR_TONE_CLASS,
  anchorStateLabel,
  anchorStateTone,
  type AnchorState,
} from '@/lib/anchor-display';

// ===== Helper functions =====

/**
 * Map color from centralized config to Tailwind classes
 */
function getColorClass(color: 'green' | 'red' | 'amber' | 'orange' | 'blue' | 'muted'): string {
  if (color === 'muted') return 'text-muted-foreground';
  return statusTextClasses[color as StatusColor] || 'text-muted-foreground';
}

/**
 * Canonical rendering of a submission's evidence period
 * (`evidenceFromDate` – `evidenceToDate`), e.g. "Apr 19 – Jul 18, 2026".
 * Single source so reviewer- and auditor-facing surfaces show the covered
 * window identically (#1425).
 */
export function formatEvidencePeriod(
  from: Date | string,
  to: Date | string,
): string {
  return `${format(new Date(from), 'MMM d')} – ${format(new Date(to), 'MMM d, yyyy')}`;
}

// ===== Sub-components =====

/**
 * Quality score badge with color coding
 */
export function QualityBadge({ score }: { score: { overallScore: number | null; qualityTier: string | null } | null | undefined }) {
  if (!score?.overallScore) return null;

  const getColor = () => {
    if (!score.qualityTier) return 'text-muted-foreground';
    const tierColors: Record<string, string> = {
      EXCELLENT: statusTextClasses.green,
      ACCEPTABLE: statusTextClasses.blue,
      NEEDS_IMPROVEMENT: statusTextClasses.orange,
      FAIL: statusTextClasses.red,
    };
    return tierColors[score.qualityTier] || 'text-muted-foreground';
  };

  const isLowQuality = score.qualityTier === 'NEEDS_IMPROVEMENT' || score.qualityTier === 'FAIL';

  return (
    <span className={cn('flex items-center gap-1 text-xs font-medium', getColor())}>
      {isLowQuality && <AlertTriangle className="h-3 w-3" />}
      {Math.round(score.overallScore)}%
    </span>
  );
}

/**
 * Due date badge with overdue/today highlighting and tooltip
 * @param date - The due date
 * @param taskStatus - Optional task status; if APPROVED, overdue styling is suppressed
 */
export function DueDateBadge({ date, taskStatus }: { date: Date | string; taskStatus?: string }) {
  const dateObj = new Date(date);
  // Don't show overdue styling for approved tasks
  const isApproved = taskStatus === 'APPROVED';
  const isOverdue = !isApproved && isPast(dateObj) && !isToday(dateObj);
  const isDueToday = !isApproved && isToday(dateObj);

  const tooltipText = isOverdue
    ? `Overdue: ${format(dateObj, 'MMM d, yyyy')}`
    : isDueToday
      ? 'Due today'
      : `Due ${format(dateObj, 'MMM d, yyyy')}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'flex items-center gap-1 text-xs cursor-help',
            isOverdue && statusTextClasses.red,
            isDueToday && statusTextClasses.orange,
            !isOverdue && !isDueToday && 'text-muted-foreground'
          )}
        >
          <Calendar className="h-3 w-3" />
          {format(dateObj, 'MMM d')}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Linear-style solid status badge: a filled colored circle with a white glyph.
 * Used for terminal states so they read at a glance (vs. faint outline icons).
 */
function FilledStatusBadge({ bg, children }: { bg: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
        bg
      )}
    >
      {children}
    </span>
  );
}

/**
 * Status icon based on ControlEvidenceStatusEnum (single source of truth)
 */
export function StatusIcon({ status, preparation, latestApiRunStatus }: { status: ControlEvidenceStatusEnum; preparation?: TestEvidencePreparation; latestApiRunStatus?: ApiCheckHealth | null }) {
  const iconClass = 'h-4 w-4 shrink-0';
  const glyphClass = 'h-2.5 w-2.5 text-white';
  // Live monitoring reflects the latest check, independent of historical review.
  if (status !== 'NOT_APPLICABLE' && preparation === 'MONITORING') {
    if (latestApiRunStatus === 'RUNNING') return <Loader2 className={`${iconClass} animate-spin ${statusTextClasses.blue}`} />;
    status = latestApiRunStatus === 'PASS' ? 'APPROVED'
      : latestApiRunStatus === 'FAIL' ? 'FAILED'
      : latestApiRunStatus === 'ERROR' || latestApiRunStatus === 'INCONCLUSIVE' ? 'INCONCLUSIVE' : 'NOT_STARTED';
  }
  // Manual evidence is complete when submitted; Control approval remains separate.
  if (status !== 'NOT_APPLICABLE' && (preparation === 'SUBMITTED' || preparation === 'LEGACY_ACCEPTED')) {
    status = 'APPROVED';
  }
  switch (status) {
    case 'APPROVED':
      return (
        <FilledStatusBadge bg="bg-status-green dark:bg-green-500">
          <Check className={glyphClass} strokeWidth={3} />
        </FilledStatusBadge>
      );
    case 'FAILED':
      return (
        <FilledStatusBadge bg="bg-status-red">
          <X className={glyphClass} strokeWidth={3} />
        </FilledStatusBadge>
      );
    case 'INCONCLUSIVE':
      return <HelpCircle className={`${iconClass} ${statusTextClasses.amber}`} />;
    // Legacy review states keep their clock when no Test preparation is supplied.
    // The pass verdict shows on the parallel health/score badge.
    case 'PASSED':
    case 'SUBMITTED':
      return <Clock className={`${iconClass} ${statusTextClasses.amber}`} />;
    case 'NEEDS_RETEST':
      return <AlertCircle className={`${iconClass} ${statusTextClasses.orange}`} />;
    case 'IN_PROGRESS':
      return <Circle className={`${iconClass} ${statusTextClasses.blue} fill-current`} />;
    case 'NOT_APPLICABLE':
      return <CircleOff className={`${iconClass} text-muted-foreground/50`} />;
    case 'NON_PERFORMING':
      return <Circle className={`${iconClass} text-muted-foreground/50`} />;
    case 'NOT_STARTED':
    default:
      return <Circle className={`${iconClass} text-muted-foreground`} />;
  }
}

/**
 * Review status text badge - shows review workflow status with tooltip
 * Uses centralized STATUS_LABELS from @deepguide-ai/types
 */
export function StatusBadgeText({
  status,
  reviewStatus: _reviewStatus,
  preparation,
  date,
  showLabel = true,
  dateTooltip,
}: {
  status: ControlEvidenceStatusEnum | string;
  preparation?: import('@deepguide-ai/types').TestEvidencePreparation;
  reviewStatus?: 'PENDING' | 'APPROVED' | 'NEEDS_REVISION' | 'WITHDRAWN' | null;
  date?: Date | string | null;
  showLabel?: boolean;
  dateTooltip?: string;
}) {
  const formattedDate = date ? format(new Date(date), 'MMM d') : null;

  // Use task status (ControlEvidenceStatus.status) as single source of truth.
  // The status field is synced on all transitions:
  // - createSubmission → SUBMITTED
  // - reviewEvidence(APPROVED) → APPROVED
  // - reviewEvidence(NEEDS_REVISION) → NEEDS_RETEST
  // - withdrawSubmission/resetStatus → IN_PROGRESS
  //
  // The reviewStatus from old submissions may be stale after reset,
  // so we ignore it and use status directly.
  const label = getStatusLabel(status, preparation);
  const color = getStatusColor(status);

  if (!showLabel && !formattedDate) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('text-xs cursor-help', getColorClass(color))}>
          {showLabel && label}
          {formattedDate && (
            <span
              className={cn('text-muted-foreground', showLabel && 'ml-1')}
            >
              {showLabel && '· '}
              {formattedDate}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {dateTooltip ?? (showLabel
          ? `Status: ${label}${formattedDate ? ` · Last submitted at ${formattedDate}` : ''}`
          : `Last submitted at ${formattedDate}`)}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * API check health badge — the latest automated-check verdict for an API test,
 * shown as a compact icon on the PARALLEL axis to the compliance/submission
 * status (RFC-189). A red/errored check does not change where the test sits in
 * the review workflow; this just tells the user the automation's last word.
 *
 * ERROR (couldn't run — access/config) is deliberately distinct from FAIL (ran,
 * control not met): the former means the automation itself is broken, which the
 * compliance status can never express.
 */
const API_CHECK_HEALTH_CONFIG: Record<
  ApiCheckHealth,
  { Icon: typeof CheckCircle2; className: string; label: string; tooltip: string; spin?: boolean }
> = {
  PASS: {
    Icon: CheckCircle2,
    className: statusTextClasses.green,
    label: 'Passing',
    tooltip: 'Automated check passing on its last run',
  },
  FAIL: {
    Icon: XCircle,
    className: statusTextClasses.red,
    label: 'Failing',
    tooltip: 'Automated check failing on its last run',
  },
  ERROR: {
    Icon: AlertTriangle,
    className: statusTextClasses.amber,
    label: 'Check error',
    tooltip: "Automated check couldn't run. Check access/configuration",
  },
  INCONCLUSIVE: {
    Icon: HelpCircle,
    className: 'text-muted-foreground',
    label: 'Inconclusive',
    tooltip: 'Automated check inconclusive on its last run',
  },
  RUNNING: {
    Icon: Loader2,
    className: statusTextClasses.blue,
    label: 'Running',
    tooltip: 'Automated check is running',
    spin: true,
  },
};

export function ApiCheckHealthBadge({ health, showLabel = true, showIcon = true }: { health: ApiCheckHealth; showLabel?: boolean; showIcon?: boolean }) {
  const config = API_CHECK_HEALTH_CONFIG[health];
  if (!config) return null;
  const { Icon, className, label, tooltip, spin } = config;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span aria-label={label} className={cn('flex shrink-0 items-center gap-1 text-xs cursor-help', className)}>
          {showIcon && <Icon aria-hidden className={cn(showLabel ? 'h-3.5 w-3.5' : 'h-4 w-4', spin && 'animate-spin')} />}
          {showLabel && label}
        </span>
      </TooltipTrigger>
      <TooltipContent>Automated check: {tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Person avatar with optional name and tooltip
 */
export function PersonAvatar({
  person,
  showName = false,
  ring = false,
  className,
  tooltipPrefix,
}: {
  person: { name: string | null; image: string | null };
  showName?: boolean;
  ring?: boolean;
  className?: string;
  tooltipPrefix?: string;
}) {
  const displayName = person.name || 'Unknown';

  const avatar = (
    <div className={cn('flex items-center gap-2', tooltipPrefix && 'cursor-help', className)}>
      <MemberAvatar
        person={{ name: person.name, image: person.image }}
        size="sm"
        className={cn(ring && 'ring-2 ring-primary/20')}
      />
      {showName && person.name && (
        <span className="block text-xs text-muted-foreground max-w-24 whitespace-normal break-words leading-tight">
          {person.name}
        </span>
      )}
    </div>
  );

  if (tooltipPrefix) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{avatar}</TooltipTrigger>
        <TooltipContent>{tooltipPrefix}: {displayName}</TooltipContent>
      </Tooltip>
    );
  }

  return avatar;
}

/**
 * Control pill displaying `Framework:TrustID` (e.g. "HIPAA:§164.308(a)(5)(ii)(A)").
 *
 * Single source of truth for the framework/control reference badge — used on the
 * control detail page, system overlay, control tree, and evidence metadata.
 * Prefer `frameworkSlug` when you have it (it gets upper-cased); otherwise pass a
 * ready-to-display `frameworkName` like "SOC 2".
 */
export interface ControlPillProps {
  /** Framework slug (e.g. "soc2", "iso27001"); upper-cased for display. Takes precedence over frameworkName. */
  frameworkSlug?: string | null;
  /** Framework display name (e.g. "HIPAA", "SOC 2") */
  frameworkName?: string | null;
  /** Control identifier (e.g., "CC6.1", "§164.308(a)(5)(ii)(A)") */
  controlId: string;
  /** Optional display identifier override */
  displayControlId?: string;
  /** Visual variant — 'primary' (blue, default) or 'secondary' (muted, e.g. "also satisfies") */
  variant?: 'primary' | 'secondary';
  /** Optional node rendered before the label (e.g. an "Also" tag) */
  prefix?: ReactNode;
  /** Native hover-tooltip text (e.g. the full control title) */
  title?: string;
  /** Optional click handler — renders the pill as an interactive button */
  onClick?: () => void;
  /** Additional class names (overrides size/spacing via tailwind-merge) */
  className?: string;
}

export function ControlPill({
  frameworkSlug,
  frameworkName,
  controlId,
  displayControlId,
  variant = 'primary',
  prefix,
  title,
  onClick,
  className,
}: ControlPillProps) {
  const frameworkDisplay = frameworkSlug?.toUpperCase() || frameworkName || '';
  const badgeControlId = displayControlId || controlId;
  const label = frameworkDisplay ? `${frameworkDisplay}:${badgeControlId}` : badgeControlId;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-normal',
        variant === 'primary'
          ? 'border-status-blue-border bg-status-blue-subtle text-status-blue-text'
          : 'border-[#E0E0DE] bg-[#F6F6F4] text-text-subtle',
        onClick && 'cursor-pointer hover:bg-[#E2EDFB]',
        className
      )}
      title={title}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      {prefix}
      {label}
    </span>
  );
}

/**
 * RFC-214 §10.3: the automated-check progress under a requirement anchor.
 *
 * One API check stays one CES; the producers collapse under their anchor rather
 * than merging into it. This renders the anchor's derived state plus accepted
 * producer progress when available. The requirement tree retains its count-only
 * fallback because its children are already visible in that read model.
 */
/**
 * The anchor's derived state as a chip — the one place that markup lives.
 *
 * Every surface that shows covering checks (the requirement tree, the auditor's
 * test table, the test detail page) renders the same label + tone, so a second
 * hand-rolled copy is how they drift apart.
 */
export function AnchorStateChip({
  anchorState,
  className,
}: {
  anchorState: string | undefined
  className?: string
}) {
  const state = (anchorState ?? 'NEEDS_MANUAL_EVIDENCE') as AnchorState;
  return (
    <span
      className={cn(
        'rounded-sm px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
        ANCHOR_TONE_CLASS[anchorStateTone(state)],
        className,
      )}
    >
      {anchorStateLabel(state)}
    </span>
  );
}

export function AnchorCheckSummary({
  anchorState,
  coveringCount,
  passedCount,
}: {
  anchorState: string | undefined
  coveringCount: number
  passedCount?: number
}) {
  return (
    <>
      <AnchorStateChip anchorState={anchorState} />
      <span
        className={cn(
          'text-[11px]',
          passedCount === coveringCount
            ? 'text-status-green'
            : 'text-text-subtle'
        )}
      >
        {passedCount == null
          ? `${coveringCount} automated ${coveringCount === 1 ? 'check' : 'checks'}`
          : `${passedCount}/${coveringCount} passed`}
      </span>
    </>
  )
}
